import React, { useEffect, useState, useContext } from 'react';
import { useStore, ExperienceContext } from '@thd-nucleus/experience-context';
import { QueryProvider, useDataModel, useLazyDataModel } from '@thd-nucleus/data-sources';
import { string, bool, number } from 'prop-types';
import { Col, Row } from '@thd-olt-component-react/core-ui';
import className from 'classnames';
import { dynamicRecsDataModel } from '../dataModel';
import { RecommendationsProductPodCarousel } from '../core/RecommendationsProductPodCarousel';
import { isSearchLoading, getAppId, getSkippers } from '../utils/helpers';

const SearchViewedRecs = ({
  anchorId,
  anchorImage,
  maxItems,
  requestKey,
  showLoading,
  hideFavorites,
  itemIds
}) => {
  const { storeId, storeZip: zipCode, isLocalized } = useStore();
  const { channel, isConsumerApp } = useContext(ExperienceContext);
  const [data, setData] = useState(null);
  const [searchSchema, setSearchSchema] = useState(null);
  const schemaName = 'searchViewed';
  const multiSchemaName = 'plpsearch_multi';

  const searchOpts = {
    variables: {
      anchorId,
      appId: getAppId(channel, isConsumerApp),
      apiName: schemaName,
      dataSource: schemaName,
      key: requestKey,
      storeId,
      zipCode,
      serviceType: null,
      ...getSkippers({}),
      maxResults: null
    },
  };
  const { data: searchData, loading, error } = useDataModel('recs', searchOpts);

  const { recs } = searchData || {};
  const multiOpts = {
    manual: true,
    variables: {
      anchorId: itemIds,
      appId: getAppId(channel, isConsumerApp),
      apiName: multiSchemaName,
      dataSource: multiSchemaName,
      key: requestKey,
      serviceType: 'mutlianchor',
      storeId,
      zipCode,
      ...getSkippers({}),
      maxResults: null
    },
  };

  const [
    getMultiProductSkus,
    {
      data: multiData,
      loading: multiLoading,
      error: multiError,
    }
  ] = useLazyDataModel('recs', multiOpts);

  const shouldRequestFallback = () => {
    return !loading && (searchData || error) && !multiLoading && !multiError && !multiData && !data;
  };

  useEffect(() => {
    if (searchData?.recs?.products?.length > 0) {
      setData(searchData);
      setSearchSchema(recs?.metadata?.apiName);
    } else if (shouldRequestFallback()) {
      getMultiProductSkus();
    }
  }, [searchData]);

  useEffect(() => {
    if (!data && multiData?.recs?.products?.length > 0) {
      setData(multiData);
      setSearchSchema(multiData?.recs?.metadata?.apiName);
    }
  }, [multiData]);

  const wrapperClasses = className(
    'dynamic-recs',
    searchSchema,
    {
      loading: showLoading && loading,
    }
  );

  if (multiError || (!loading && !multiLoading && !data
    && multiData !== undefined && !searchData?.recs && !multiData?.recs)) {
    return null;
  }

  return (
    <Row>
      <Col id={searchSchema} className={wrapperClasses} data-type="container">
        <meta data-prop="name" content={searchSchema} />
        <RecommendationsProductPodCarousel
          anchorImage={anchorImage}
          loading={isSearchLoading(data, searchData, multiData, loading, multiLoading)}
          hideFavorites={hideFavorites}
          itemClass="fixed-item"
          scheme={searchSchema}
          storeId={storeId}
          zipCode={zipCode}
          maxItems={maxItems}
          showLoading={showLoading}
          data={data?.recs}
        />
      </Col>
    </Row>
  );
};

SearchViewedRecs.dataModel = dynamicRecsDataModel;

SearchViewedRecs.displayName = 'SearchViewedRecs';

SearchViewedRecs.propTypes = {
  anchorId: string.isRequired,
  anchorImage: string,
  hideFavorites: bool,
  requestKey: string,
  maxItems: number,
  showLoading: bool,
  itemIds: string,
};

SearchViewedRecs.defaultProps = {
  anchorImage: null,
  hideFavorites: true,
  requestKey: null,
  showLoading: true,
  maxItems: 16,
  itemIds: ''
};

export default SearchViewedRecs;
